import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, X, ChevronLeft, ChevronRight, Ban } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import UserTable from '../../components/ViewAllUsers/UserTable';
import UserDetailSidebar from '../../components/ViewAllUsers/UserDetailSidebar';
import UserReassignmentModal from '../../components/ViewAllUsers/UserReassignmentModal';
import { User } from "../../components/types/user";
import { FloatingActions } from '../../components/newUserForm';
import { getAllUsers, updateUserBlockStatus } from '../../services/usersapi';
import { toast } from 'sonner';
import { UnblockRequestsModal } from '../../components/UnblockRequestsModal';
import { getUnblockRequestsForReview } from '../../services/unblockRequestApi';
import { useRbac } from '../../context';

const UsersView = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: '',
    blockStatus: '',
    activityRange: ''
  });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [users, setUsers] = useState<User[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
  const [userToReassign, setUserToReassign] = useState<User | null>(null);
  const [userAdded,setUserAdded] = useState(false);
  const [userEdited,setUserEdited] = useState(false);
  const [blockLoading, setBlockLoading] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showUnblockRequestsModal, setShowUnblockRequestsModal] = useState(false);
  const [pendingUnblockCount, setPendingUnblockCount] = useState(0);
  const { user } = useRbac();

  const activeFilters = Object.entries(filters).filter(([_, value]) => value !== '');

  const activityFilterOptions = [
    { value: 'active', label: 'Active (last 24 hours)' },
    { value: '1d', label: 'Inactive for 1 day' },
    { value: '5d', label: 'Inactive for 5 days' },
    { value: '15d', label: 'Inactive for 15 days' },
    { value: '30d', label: 'Inactive for 30 days' },
    { value: '3m', label: 'Inactive for 3 months' },
    { value: '6m', label: 'Inactive for 6 months' }
  ];

  const activityFilterLabelMap = activityFilterOptions.reduce<Record<string, string>>((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {});

  // Handler for sorting
  const onSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Handler for page change to pass to UserTable
  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  const removeFilter = (key: string) => {
    setFilters(prev => ({ ...prev, [key]: '' }));
    setCurrentPage(1); // Reset to first page when removing filter
  };

  const clearAllFilters = () => {
    setFilters({ role: '', blockStatus: '', activityRange: '' });
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Block/Unblock user handler
  const handleBlockToggle = async (user: User) => {
    if (!user) return;
    
    setBlockLoading(user.id);
    const newBlockedStatus = !user.isBlocked;
    
    try {
      // Call API to update user block status
      await updateUserBlockStatus(user.id, newBlockedStatus);
      
      toast.success(newBlockedStatus ? 'User blocked successfully' : 'User unblocked successfully');
      
      // Update users list locally
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === user.id ? { ...u, isBlocked: newBlockedStatus } : u
        )
      );

      // Update selected user if it's the same user
      if (selectedUser && selectedUser.id === user.id) {
        setSelectedUser({ ...selectedUser, isBlocked: newBlockedStatus });
      }
    } catch (error) {
      console.error('Failed to update user status:', error);
      toast.error('Failed to update user status');
    } finally {
      setBlockLoading(null);
    }
  };

  // Bulk block/unblock users
  const handleBulkBlockToggle = async (action: 'block' | 'unblock') => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users to perform bulk action');
      return;
    }

    setBulkActionLoading(true);
    const selectedUserObjects = users.filter(user => selectedUsers.includes(user.id));
    const isBlocking = action === 'block';
    
    try {
      // Process each user
      const promises = selectedUserObjects.map(user => 
        updateUserBlockStatus(user.id, isBlocking)
      );
      
      await Promise.all(promises);
      
      // Update users list locally
      setUsers(prevUsers => 
        prevUsers.map(u => 
          selectedUsers.includes(u.id) 
            ? { ...u, status: isBlocking ? 'blocked' : 'active' } 
            : u
        )
      );

      // Update selected user if it's affected
      if (selectedUser && selectedUsers.includes(selectedUser.id)) {
        setSelectedUser({ ...selectedUser, isBlocked: isBlocking });
      }

      toast.success(`Successfully ${action}ed ${selectedUsers.length} user(s)`);
      setSelectedUsers([]); // Clear selection
    } catch (error) {
      console.error(`Failed to ${action} users:`, error);
      toast.error(`Failed to ${action} users`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Select/deselect all users on current page
  const handleSelectAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id));
    }
  };

  // Toggle user selection
  const handleUserSelect = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Fetch users from backend with query parameters
  const fetchUsers = async () => {
    setIsLoading(true);
      console.log('Fetching users...',filters.blockStatus);

    try {
      const queryParams = {
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        search: searchTerm,
        role: filters.role,
        blockStatus: filters.blockStatus==='active' ? false : filters.blockStatus==='blocked'? true: undefined,
        activityRange: filters.activityRange || undefined,
      };
      console.log("queryParams",queryParams);

      const response = await getAllUsers(queryParams);
      console.log('Users response:', response);
      console.log('First user profilePhotoUrl:', response.data?.users?.[0]?.profilePhotoUrl);
      setUsers(response.data.users || []);
      setTotalResults(response.totalResults || response.data.users?.length || 0);
      if(selectedUser){
        const updatedSelectedUser = response.data.users.find((user: User) => user.id === selectedUser.id);
        if(updatedSelectedUser){
          setSelectedUser(updatedSelectedUser);
        } else {
          setSelectedUser(null); // User might have been deleted
        }
      }
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if(userAdded || userEdited){
  fetchUsers();
  setUserAdded(false);
  setUserEdited(false);
}
  }, [userAdded,userEdited]);

  // Fetch pending unblock requests count
  const fetchPendingUnblockCount = async () => {
    if (user?.role === 'SHM' || user?.role === 'SHM_ADMIN' || user?.role === 'AGENCY_ADMIN' || user?.role === 'FBDE_ADMIN' || user?.role === 'ADMIN') {
      try {
        const response = await getUnblockRequestsForReview(1, 100, 'pending');
        if (response.success) {
          setPendingUnblockCount(response.data.requests.length);
        }
      } catch (error) {
        console.error('Failed to fetch pending unblock count:', error);
      }
    }
  };

  useEffect(() => {
    fetchPendingUnblockCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchPendingUnblockCount, 30000);
    return () => clearInterval(interval);
  }, [user?.role]);

  useEffect(() => {
  fetchUsers();

  }, [currentPage, itemsPerPage, searchTerm, filters.role, filters.blockStatus, filters.activityRange]);

  const handleReassignUser = (user: User) => {
    setUserToReassign(user);
    setIsReassignModalOpen(true);
  };

  const handleReassignSuccess = () => {
    // Refresh the users list
    const fetchUsers = async () => {
      
      setIsLoading(true);
      try {
        const queryParams = {
          page: currentPage.toString(),
          limit: itemsPerPage.toString(),
          search: searchTerm,
          role: filters.role,
          blockStatus: filters.blockStatus==='active' ? false : filters.blockStatus==='blocked'? true:undefined,
          activityRange: filters.activityRange || undefined,
        };

        const response = await getAllUsers(queryParams);
        setUsers(response.data.users || []);
        setTotalResults(response.totalResults || response.data.users?.length || 0);
        setTotalPages(response.totalPages || 1);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        toast.error('Failed to load users');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  };

  // Handle user updates from sidebar
  const handleUserUpdate = (updatedUser: User) => {
    setUsers(prevUsers => 
      prevUsers.map(u => 
        u.id === updatedUser.id ? updatedUser : u
      )
    );
    setSelectedUser(updatedUser);
  };

  const renderPagination = () => {
    const pageNumbers = [];
    const maxPagesToShow = 6;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1 || isLoading}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        {pageNumbers.map(page => (
          <Button
            key={page}
            variant={currentPage === page ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCurrentPage(page)}
            disabled={isLoading}
            className={currentPage === page ? 'bg-[#EC6E51] text-primary-foreground' : ''}
          >
            {page}
          </Button>
        ))}

        {endPage < totalPages && (
          <>
            <span className="text-sm text-muted-foreground">...</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={isLoading}
            >
              {totalPages}
            </Button>
          </>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages || isLoading}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-4 bg-background">
      <div className="sticky top-0 z-40 bg-background border-b border-border">
        <div className="px-6 py-4">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search by name, email, phone or role"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-10"
                  style={{ maxWidth: "450px" }}
                />
              </div>
              <Select
                value={filters.role || 'all'}
                onValueChange={(value) => {
                  setFilters(prev => ({ ...prev, role: value === 'all' ? '' : value }));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="HR">HR</SelectItem>
                  <SelectItem value="HM">HM</SelectItem>
                  <SelectItem value="AGENCY">Agency</SelectItem>
                  <SelectItem value="SHM">SHM</SelectItem>
                  <SelectItem value="SHM_ADMIN">SHM Admin</SelectItem>
                  <SelectItem value="AGENCY_ADMIN">Agency Admin</SelectItem>
                  <SelectItem value="FBDE_ADMIN">FBDE Admin</SelectItem>
                  <SelectItem value="FBDE">FBDE</SelectItem>
                  <SelectItem value="ADMIN">ADMIN</SelectItem>
                  <SelectItem value="TPO">TPO</SelectItem>
                  <SelectItem value="FREELANCE_COUNSELLOR">Freelance Counsellor</SelectItem>
                  <SelectItem value="BDE">BDE</SelectItem>
                  <SelectItem value="CONSULTANT">CONSULTANT</SelectItem>
                  <SelectItem value="MODERATOR">MODERATOR</SelectItem>
                  <SelectItem value="MASTER_BDE">Master BDE</SelectItem>
                  <SelectItem value="FREELANCE_BDE">Freelance BDE</SelectItem>
                  <SelectItem value="BDE_INTERN">BDE Intern</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.blockStatus || 'all'}
                onValueChange={(value) => {
                  setFilters(prev => ({ ...prev, blockStatus: value === 'all' ? '' : value }));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.activityRange || 'all'}
                onValueChange={(value) => {
                  setFilters(prev => ({ ...prev, activityRange: value === 'all' ? '' : value }));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Inactive For" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {activityFilterOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-3">
                {(user?.role === 'SHM' || user?.role === 'SHM_ADMIN' || user?.role === 'AGENCY_ADMIN' || user?.role === 'ADMIN') && (
                  <Button
                    onClick={() => {
                      setShowUnblockRequestsModal(true);
                      fetchPendingUnblockCount(); // Refresh count when opening modal
                    }}
                    className="bg-white hover:bg-orange-50 text-orange-600 border-2 border-orange-600 relative"
                  >
                    Unblock Requests
                    {pendingUnblockCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {pendingUnblockCount > 99 ? '99+' : pendingUnblockCount}
                      </span>
                    )}
                  </Button>
                )}
                <FloatingActions userAdded={userAdded} setUserAdded={setUserAdded}/>
              </div>
            </div>

            {(activeFilters.length > 0 || searchTerm) && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-muted-foreground">Active filters:</span>
                {searchTerm && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Search: {searchTerm}
                    <X
                      className="w-3 h-3 cursor-pointer"
                      onClick={() => {
                        setSearchTerm('');
                        setCurrentPage(1);
                      }}
                    />
                  </Badge>
                )}
                {activeFilters.map(([key, value]) => {
                  const displayKey =
                    key === 'blockStatus'
                      ? 'Status'
                      : key === 'activityRange'
                        ? 'Inactive For'
                        : key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
                  const displayValue =
                    key === 'blockStatus'
                      ? value === 'blocked'
                        ? 'Blocked'
                        : 'Active'
                      : key === 'activityRange'
                        ? activityFilterLabelMap[value as string] || value
                        : value;
                  return (
                    <Badge key={key} variant="secondary" className="flex items-center gap-1">
                      {displayKey}: {displayValue}
                      <X
                        className="w-3 h-3 cursor-pointer"
                        onClick={() => removeFilter(key)}
                      />
                    </Badge>
                  );
                })}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-xs"
                >
                  Clear all
                </Button>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 justify-end" style={{ width: '100%' }}>
             
               
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-275px)] overflow-y-scroll">
        <div className={`flex-1 transition-all duration-300 ${selectedUser ? 'mr-96' : ''}`}>
          <UserTable
            users={users}
            setUsers={setUsers}
            isloading={isLoading}
            onUserSelect={setSelectedUser}
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={onSort}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            onUserReassign={handleReassignUser}
            onBlockToggle={handleBlockToggle}
            blockLoading={blockLoading}
            onSourceChange={() => {}}
          />
        </div>
        <UserDetailSidebar
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          setUserEdited={setUserEdited}

        />
      </div>
      <div className="flex flex-col gap-2 px-5 pb-5 md:flex-row md:items-center md:justify-between">
        <span className="text-sm text-muted-foreground">
          Showing {isLoading ? '...' : users.length} of {isLoading ? '...' : totalResults} users
        </span>
        {renderPagination()}
      </div>

      <UserReassignmentModal
        isOpen={isReassignModalOpen}
        onClose={() => setIsReassignModalOpen(false)}
        user={userToReassign}
        onSuccess={handleReassignSuccess}
      />

      {/* Unblock Requests Modal */}
      <UnblockRequestsModal
        isOpen={showUnblockRequestsModal}
        onClose={() => setShowUnblockRequestsModal(false)}
        onRequestUpdated={fetchPendingUnblockCount}
      />
    </div>
  );
};

export default UsersView;